import { io, Socket } from 'socket.io-client';
import { store } from 'store';
import { logoutUser } from 'store/authSlice';

import axiosInstance from 'components/api/axios';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function getSocketToken(baseUrl?: string, retries = 3): Promise<string | null> {
  try {
    const response = await axiosInstance.get('/admin/auth/socket-token', {
      baseURL: baseUrl,
    });
    return response.data?.data?.token || response.data?.token;
  } catch (error) {
    console.error(`SocketService: Failed to fetch socket token. Retries left: ${retries}`, error);

    if (retries > 0) {
      await delay(1500);
      return getSocketToken(baseUrl, retries - 1);
    }
    return null;
  }
}

class SocketService {
  private socket: Socket | null = null;

  private readonly baseUrl: string = process.env.REACT_APP_API_URL || '';

  public async connect(): Promise<Socket> {
    if (this.socket?.connected) {
      return this.socket;
    }

    let token = null;
    token =
      process.env.NODE_ENV === 'development'
        ? await getSocketToken()
        : await getSocketToken(this.baseUrl);

    const socketOptions: any = {
      path: '/admin-socket/',
      transports: ['websocket'],
      withCredentials: true,
      reconnection: true,
    };

    if (token) {
      socketOptions.auth = { token };
    }

    this.socket = io(this.baseUrl, socketOptions);

    this.socket.on('connect', () => {
      console.log('Socket connected');
    });

    this.socket.on('connect_error', async (error) => {
      console.log(error, 'connect_error');

      const isExpired =
        (error as any).data?.code === 'SOCKET_TOKEN_EXPIRED' ||
        error.message?.includes('invalid or expired socket token') ||
        error.message?.includes('SOCKET_TOKEN_EXPIRED') ||
        error.message?.includes('Unauthorized');

      if (isExpired) {
        console.log('Socket token expired. Refreshing socket token...');
        const newToken =
          process.env.NODE_ENV === 'development'
            ? await getSocketToken()
            : await getSocketToken(this.baseUrl);

        if (newToken && this.socket) {
          this.socket.disconnect();

          this.socket.auth = {
            ...this.socket.auth,
            token: newToken,
          };

          this.socket.connect();
        } else {
          console.error(
            'SocketService: Failed to retrieve new socket token after expiration. Retrying connection in 3 seconds...',
          );
          setTimeout(() => {
            if (this.socket && !this.socket.connected) {
              this.socket.connect();
            }
          }, 3000);
        }
      }
    });

    this.socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
    });

    return this.socket;
  }

  public disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  public getSocket(): Socket | null {
    return this.socket;
  }

  public on(event: string, callback: (...args: any[]) => void): void {
    this.socket?.on(event, callback);
  }

  public off(event: string, callback?: (...args: any[]) => void): void {
    this.socket?.off(event, callback);
  }

  public emit(event: string, data: any): void {
    this.socket?.emit(event, data);
  }
}

export const socketService = new SocketService();
